import React from "react";
import { Accordion, Alert, Form, FormControl } from "react-bootstrap";
import { Answers } from "./Answers";
import { QuestionInterface, AnswerInterface, PendingCommand, ApiHelper } from "../../helpers";
import { AnswerArray } from "./AnswerArray";
import { MessageAnswer, PlaylistAnswer } from ".";
import { DesignerOverlay } from "../designer/DesignerOverlay";
import { CanvaModal } from "../canva/CanvaModal";
import { DragDropContext, Draggable, Droppable, DropResult } from "@hello-pangea/dnd";

interface Props {
  subscriptionData?: Record<string, unknown>,
  question: QuestionInterface,
  answer: AnswerInterface,
  allQuestions: QuestionInterface[],
  allAnswers: AnswerInterface[],
  updateValue: (questionId: number, newValue: string) => void,
  sendCommand: (cmd: PendingCommand) => void
}

export const Answer: React.FC<Props> = props => {

  const [showDesigner, setShowDesigner] = React.useState(false);
  const [showCanva, setShowCanva] = React.useState(false);
  const desc = (props.question.description) ? <> <span className="description">({props.question.description})</span></> : null;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const val = e.currentTarget.value;
    props.updateValue(props.question.id, val);
  }

  const wrapFieldType = (contents: React.ReactElement | React.ReactElement[]) => {
    return (<Form.Group className="mb-3">
      <label>{props.question.title}{desc}</label>
      {contents}
    </Form.Group>);
  }


  const getHeader = () => {
    const childElements = (props.question.children?.length > 0) ? <Answers questions={props.question.children} answers={props.allAnswers} sendCommand={props.sendCommand} updateValue={props.updateValue} /> : null;
    return (<Accordion.Item eventKey={props.question.title} className="answerCard">
      <Accordion.Header>{props.question.title}</Accordion.Header>
      <Accordion.Body>{childElements}</Accordion.Body>
    </Accordion.Item>);
  }

  const getArray = () => {
    return (<AnswerArray question={props.question} answer={props.answer} allQuestions={props.allQuestions} updateValue={props.updateValue} sendCommand={props.sendCommand} />);
  }

  const getPlaylist = (includeMessages: boolean) => {
    return wrapFieldType(<PlaylistAnswer question={props.question} answer={props.answer} updateValue={props.updateValue} includeMessages={includeMessages} />);
  }

  const getMessage = () => {
    return <MessageAnswer question={props.question} answer={props.answer} updateValue={props.updateValue} />;
  }

  const getString = () => {
    return wrapFieldType(<FormControl placeholder={props.question.placeholder} value={props.answer?.value} onChange={handleChange} data-testid={`question-${props.question.keyName}`} />);
  }

  const getHidden = () => {
    return <></>;
  }

  const getLabel = () => {
    const subVal = (props.subscriptionData) ? props.subscriptionData[props.question.keyName] : ""
    return wrapFieldType(<b> {props.answer?.value || subVal?.toString() || props.question.placeholder}</b>);
  }

  const getText = () => {
    return wrapFieldType(<FormControl as="textarea" placeholder={props.question.placeholder} value={props.answer?.value} onChange={handleChange} style={{ minHeight: 300 }} data-testid={`question-${props.question.keyName}`} />);
  }



  const getList = () => {
    const options: React.ReactElement[] = [];
    const pairs = props.question.choices.split("|");
    pairs.forEach(p => {
      const set = p.split("`");
      if (set.length === 2) options.push(<option value={set[0]}>{set[1]}</option>);
    });
    if (props.question && props.answer && !props.answer.value && pairs.length > 0) {
      const newVal = pairs[0].split("`")[0];
      if (newVal) props.updateValue(props.question.id, newVal);
    }
    return wrapFieldType(<FormControl data-testid={`answer-select-${props.question.keyName}`} as="select" value={props.answer?.value} onChange={handleChange}>{options}</FormControl>);
  }

  const getBool = () => {
    const checked = props.answer?.value?.toLowerCase() === "true";
    const handler = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.preventDefault();
      const val = e.currentTarget.checked ? "True" : "False";
      setTimeout(() => { props.updateValue(props.question.id, val); }, 50);  //Browser doesn't show update immediately if we don't use setTimeout
    }
    return (<Form.Group className="mb-3">
      <div className="form-check">
        <input data-testid={`answer-checkbox-${props.question.keyName}`} type="checkbox" className="form-check-input" checked={checked} onChange={handler} />
        <label className="form-check-label">{props.question.title}{desc}</label>
      </div>
    </Form.Group>);
  }

  const getImage = () => {
    const clickHandler = (e: React.MouseEvent) => {
      e.preventDefault();
      const cmd: PendingCommand = {
        action: "editImage",
        parameter: { question: props.question, answer: props.answer || props.question.placeholder }
      }
      props.sendCommand(cmd);
    };

    return wrapFieldType(<div><a href="about:blank" onClick={clickHandler}>
      <img src={props.answer?.value || props.question.placeholder} className="imageField" alt="thumbnail" />
    </a></div>);
  }

  const handleFileChange = () => {
    props.updateValue(props.question.id, "Pending Upload");
  }

  const handleFilesChange = () => {
    let files = props.answer?.value?.split("`");
    if (!files) files = [];
    if (files.length === 0 || files[files.length - 1] !== "Pending Upload") files.push("Pending Upload");
    props.updateValue(props.question.id, files.join("`"));
  }

  const handleFileDelete = (index: number) => {
    const files = props.answer?.value?.split("`");
    const fileName = files.splice(index, 1)[0];
    ApiHelper.delete("/files/messages/" + props.answer?.id + "?fileName=" + encodeURI(fileName));
    props.updateValue(props.question.id, files.join("`"));
  }

  const getFile = () => {
    const fileId = "question" + props.question.id + "file";
    const parts = props.answer?.value?.split("/");
    const displayName = parts[parts.length - 1];
    const displayLink = (props.answer?.value?.startsWith("http")) ? <a href={props.answer.value}>{displayName}</a> : <>{displayName}</>;
    const accept = props.question.choices ? props.question.choices : "*.*";

    return wrapFieldType(<>
      <div>{displayLink}</div>
      <div>
        <a href="about:blank" onClick={(e) => { e.preventDefault(); document.getElementById(fileId).click(); }} className="btn btn-primary" >Upload</a>
        <input data-testid={`answer-file-upload-${props.question.keyName}`} type="file" accept={accept} style={{ display: "none" }} id={fileId} onChange={handleFileChange} />
      </div>
    </>);
  }




  const getItemStyle = (isDragging: boolean, draggableStyle: React.CSSProperties | undefined) => {
    return {
      opacity: isDragging ? 0.5 : 1,
      ...draggableStyle
    }
  }

  const getContainerStyle = (data: string) => {
    if (data) return { paddingBottom: 100 }
    else return {}
  }

  const onDragEnd = (result: DropResult) => {
    if (!result.destination) return;
    let files = props.answer?.value?.split("`");
    if (!files) files = [];
    const [file] = files.splice(result.source.index, 1);
    files.splice(result.destination.index, 0, file);
    props.updateValue(props.question.id, files.join("`"));
  }

  const getFiles = () => {
    const fileId = "question" + props.question.id + "file";
    const fileList: { name: string, url: string }[] = [];
    let splitAnswer = (props.answer?.value?.indexOf("`") > -1)
      ? props.answer?.value?.split("`")
      : props.answer?.value?.split(",");
    if (!splitAnswer) splitAnswer = []
    splitAnswer.forEach(f => {
      const parts = f.split("/");
      fileList.push({ name: parts[parts.length - 1], url: f });
    })

    const items: React.ReactElement[] = [];
    let idx = 0;
    for (let f of fileList) {
      if (f.name !== "") {
        const index = idx;
        const deleteLink = (f.name === "Pending Upload")
          ? null
          : <a href="about:blank" onClick={(e) => { e.preventDefault(); handleFileDelete(index) }}>
            <i className="far fa-trash-alt text-danger"></i>
          </a>;
        items.push(
          <Draggable key={f.name} draggableId={f.name} index={idx}>
            {(provided, snapshot) => (
              <div className="list-group-item list-group-item-action" ref={provided.innerRef} {...provided.draggableProps} style={getItemStyle(snapshot.isDragging, provided.draggableProps.style)} >
                <div className="drag-handle" {...provided.dragHandleProps} title="Drag to reorder">
                  <i className="fas fa-grip-vertical"></i>
                </div>
                <a href={f.url}>{f.name}</a> &nbsp;
                {deleteLink}
              </div>
            )}
          </Draggable>

        );
      }
      idx++;
    }

    const accept = props.question.choices ? props.question.choices : "*.*";


    return wrapFieldType(<>
      <DragDropContext onDragEnd={onDragEnd}>
        <Droppable droppableId="files" >
          {(provided, snapshot) => (
            <div id="answerFiles" className="list-group" {...provided.droppableProps} ref={provided.innerRef} style={getContainerStyle(snapshot.draggingFromThisWith)} >
              <>{items}</>
            </div>
          )}
        </Droppable>
      </DragDropContext>
      <div style={{ marginTop: 20 }}>
        <a href="about:blank" onClick={(e) => { e.preventDefault(); document.getElementById(fileId).click(); }} className="btn btn-primary" >Upload</a>
        <input data-testid={`answer-files-upload-${props.question.keyName}`} type="file" accept={accept} style={{ display: "none" }} id={fileId} onChange={handleFilesChange} multiple={true} />
      </div>
    </>);
  }


  const getVideo = () => {
    const fileId = "question" + props.question.id + "file";
    let displayContent = <>None</>;
    if (props.answer?.value === "Pending Upload") displayContent = <Alert variant="info"><b>Pending Upload</b> - Click 'Save' to continue</Alert>;
    else if (props.answer?.value) {
      const imgSrc = props.answer.value.replace(".webm", "thumb.jpg").replace(".mp4", "thumb.jpg");
      displayContent = <img src={imgSrc} alt="thumbnail" className="imageField" />;
    }
    const accept = "*.mp4,*.webm";

    return wrapFieldType(<>
      <div style={{ marginBottom: 10 }} >{displayContent}</div>
      <div>
        <a href="about:blank" onClick={(e) => { e.preventDefault(); document.getElementById(fileId).click(); }} className="btn btn-primary" >Upload</a>
        <input data-testid={`answer-single-file-upload-${props.question.keyName}`} type="file" accept={accept} style={{ display: "none" }} id={fileId} onChange={handleFileChange} />
      </div>
    </>);
  }

  const getDesigner = () => {
    let jsonData = props.answer.value || props.question.placeholder;
    if (jsonData.indexOf("/default.json") > -1) {
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get("shape") === "9:16") jsonData = jsonData.replace("/default.json", "/defaultV.json?v=2");
    }

    const designer = (showDesigner) ? <DesignerOverlay jsonData={jsonData} onUpdate={(val) => { props.updateValue(props.question.id, val); setShowDesigner(false); }} /> : <></>
    return wrapFieldType(<>
      <div>
        <a href="about:blank" onClick={(e) => { e.preventDefault(); setShowDesigner(true); }} className="btn btn-primary" >Launch Designer</a>
      </div>
      {designer}
    </>);
  }

  const getCanva = () => {
    const handleCanvaSelect = (imageUrl: string) => {
      props.updateValue(props.question.id, imageUrl);
      setShowCanva(false);
    };

    const contentType = props.answer?.messageId ? "messages" :
                        props.answer?.playlistId ? "playlists" : "overlay";
    const contentId = props.answer?.messageId || props.answer?.playlistId ||
                     props.answer?.overlayId || 0;

    return wrapFieldType(<>
      <div>
        {props.answer?.value && (
          props.answer.value.toLowerCase().endsWith(".mp4")
            ? <video src={props.answer.value} className="mb-2" controls style={{ maxWidth: "100%", maxHeight: 200 }} />
            : <img src={props.answer.value} className="imageField mb-2" alt="canva design" style={{ maxWidth: "100%", maxHeight: 200 }} />
        )}
      </div>
      <div>
        <a
          href="about:blank"
          onClick={(e) => { e.preventDefault(); setShowCanva(true); }}
          style={{
            backgroundColor: "#FFFFFF",
            borderColor: "#E0E0E0",
            borderWidth: 1,
            borderStyle: "solid",
            color: "#000000",
            padding: "8px 16px",
            fontSize: "16px",
            fontWeight: 500,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            borderRadius: 8,
            boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
            whiteSpace: "nowrap",
            textDecoration: "none"
          }}
        >
          <img src="/images/canva.png" alt="Canva" style={{ width: 28, height: 28, marginRight: 12 }} />
          {props.answer?.value ? "Change Canva Design" : "Select Canva Design"}
        </a>
      </div>
      {showCanva && (
        <CanvaModal
          onClose={() => setShowCanva(false)}
          onSelect={handleCanvaSelect}
          contentType={contentType}
          contentId={contentId}
          questionId={props.question.id}
        />
      )}
    </>);
  }

  let result = <></>
  switch (props.question.fieldType) {
    case "hidden": result = getHidden(); break;
    case "header": result = getHeader(); break;
    case "label": result = getLabel(); break;
    case "array": result = getArray(); break;
    case "bool": result = getBool(); break;
    case "string": result = getString(); break;
    case "text": result = getText(); break;
    case "list": result = getList(); break;
    case "image": result = getImage(); break;
    case "file": result = getFile(); break;
    case "file[]": result = getFiles(); break;
    case "designer": result = getDesigner(); break;
    case "canva": result = getCanva(); break;
    case "message": result = getMessage(); break;
    case "playlist": result = getPlaylist(false); break;
    case "playlistOrMessage": result = getPlaylist(true); break;
    case "video":
    case "silentVideo":
      result = getVideo();
      break;
    default:
      result = wrapFieldType(<div>Not implemented: {props.question.fieldType}</div>)
      break;
  }
  return result;

}